import type { AutocompleteSearchFieldItem } from '@pcln/horizon-search-form'
import type { ReactNode } from 'react'

import type { LOCATION_SEARCH_TYPE } from '@/types'

import TypeaheadBadge from '../TypeAhead/TypeaheadBadge'
import { getTypeaheadBadge } from '../TypeAhead/utils/getTypeaheadBadge'

export type TypeaheadRowItem =
  | AutocompleteSearchFieldItem
  | {
      content: string
      displayLine1: string
      itemName: string
      type: string
      cityID?: string
      lat?: number
      lon?: number
    }

export type GetHotelTypeaheadFirstLineSuffixParams = {
  isHotelTypeahead: boolean
  popularTrendingVariant: string | undefined
}

function isBadgeableItem(item: TypeaheadRowItem): boolean {
  return 'type' in item && !('isGroupHeader' in item && item.isGroupHeader)
}

export function getHotelTypeaheadFirstLineSuffix(
  item: TypeaheadRowItem,
  { isHotelTypeahead, popularTrendingVariant }: GetHotelTypeaheadFirstLineSuffixParams,
): ReactNode {
  if (!isHotelTypeahead || !isBadgeableItem(item)) return null
  const badgeableItem = item as unknown as LOCATION_SEARCH_TYPE

  // Only mount the Horizon firstLineSuffix slot when a badge will actually
  // render. Returning a wrapper unconditionally keeps the slot present beside
  // the label, and firstLineRowSlot's `gap-1` then adds trailing space on every
  // badge-less hotel row (DEFAULT variant, non-cities, non-qualifying cities).
  if (!getTypeaheadBadge({ item: badgeableItem, variant: popularTrendingVariant })) return null

  // Horizon's firstLineRow centers the suffix (`items-center`); Figma places the
  // badge slightly above the city name's optical center, so nudge it up 2px via a
  // relative offset. Inline style (not a Tailwind arbitrary class) so it always
  // compiles into the deployed CSS regardless of Tailwind v4 source detection.
  return (
    <span style={{ display: 'inline-flex', position: 'relative', top: '-2px' }}>
      <TypeaheadBadge item={badgeableItem} variant={popularTrendingVariant} />
    </span>
  )
}
